import { toggle } from '@Util/util';
import { BudgetList } from './setting';
import * as S from './state';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import queryString from 'query-string';

const ADMISSIONS: S.AdmissionConditionKey[] = [
    'is_handicap_unnecessary',
    'is_interview_unnecessary',
    'is_enrollment_in_others_unnecessary',
    'is_accompany_impossible',
    'female_enrollable',
    'is_no_age_limit',
];
const PLAYSTYLE: S.PlaystyleKey[] = ['walkable', 'cart_usable', 'caddy_selectable'];
const PRACTICE_PLACE: S.PracticePlaceKey[] = ['has_chipping_green_for_practice', 'has_bunker_for_practice'];
const OTHER: S.OtherKey[] = [
    'has_club_bus',
    'has_professional_tournament',
    'has_registration_family_system',
    'is_course_length_more_than_6800',
    'is_held_competition_over_twenty_times',
    'recruitment',
];

const setSearchState = (
    highways: S.Highway[] = [],
    lines: S.Line[] = [],
    seriesList: S.Series[] = []
): S.SearchState => {
    const query = queryString.parse(location.search);

    // フリーワード
    const word = query['free_words[]'] ? [].concat(query['free_words[]']).join(' ') : '';

    // 入会条件
    const admissionCondition = [];
    ADMISSIONS.forEach((a) => {
        if (a in query) {
            admissionCondition.push(a);
        }
    });

    // プレースタイル
    const playstyle = [];
    PLAYSTYLE.forEach((p) => {
        if (p in query) {
            playstyle.push(p);
        }
    });

    // 練習場
    const practicePlace = [];
    PRACTICE_PLACE.forEach((p) => {
        if (p in query) {
            practicePlace.push(p);
        }
    });

    // その他
    const others = [];
    OTHER.forEach((o) => {
        if (o in query) {
            others.push(o);
        }
    });

    // 総予算
    const minBudget = BudgetList.indexOf(Number(query.total_price_minimum));
    const maxBudget =
        BudgetList.indexOf(Number(query.total_price_maximum)) > -1
            ? BudgetList.indexOf(Number(query.total_price_maximum))
            : BudgetList.length;

    // 名義書換料
    const nameTransferFee = () => {
        const min = query.registration_fee_minimum || '';
        const max = query.registration_fee_maximum || '';

        if (min === '' && max === '49') return 'under_50';
        if (min === '50' && max === '99') return 'between_50_100';
        if (min === '100' && max === '199') return 'between_100_200';
        if (min === '200' && max === '') return 'over_200';
        return '';
    };

    // 名義書換料
    const annualFee = () => {
        const min = query.annual_fee_minimum || '';
        const max = query.annual_fee_maximum || '';

        if (min === '' && max === '29000') return 'under_3';
        if (min === '30000' && max === '49999') return 'between_3_5';
        if (min === '50000' && max === '69999') return 'over_7';
        if (min === '70000' && max === '') return 'over_7';
        return '';
    };

    // 総会員数
    const memberCount = () => {
        const min = query.total_member_minimum || '';
        const max = query.total_member_maximum || '';

        if (min === '' && max === '1000') return 'under_1000';
        if (min === '1001' && max === '1500') return 'between_1001_1500';
        if (min === '1501' && max === '2000') return 'between_1500_2000';
        if (min === '2001' && max === '') return 'over_2001';
        return '';
    };

    // 高速道路
    const highway: S.Highway = highways.find((l) => Number(l.id) === Number(query.highway_id)) || {
        id: 0,
        name: '',
        distance: '',
    };

    if (query.highway_distance) {
        highway.distance = String(query.highway_distance);
    }

    // 鉄道
    const line: S.Line = lines.find((l) => Number(l.id) === Number(query.line_id)) || { id: null, name: '' };

    // グループ・系列
    const series: S.Series = seriesList.find((s) => Number(s.id) === Number(query.series_id)) || { id: null, name: '' };

    return {
        word: word,
        budget: {
            min: minBudget,
            max: maxBudget,
        },
        nameTransferFee: nameTransferFee(),
        annualFee: annualFee(),
        playstyle: playstyle,
        practicePlace: practicePlace,
        memberCount: memberCount(),
        admissionCondition: admissionCondition,
        noDeposit: !!query.no_deposit,
        others: others,
        highway: highway,
        line: line,
        series: series,
    };
};

export const InitialSearchState = (highways: S.Highway[], lines: S.Line[], seriesList: S.Series[]): S.SearchState => {
    return setSearchState(highways, lines, seriesList);
};

export type OnChangeBudgetArgs = {
    min: number;
    max: number;
};

export type SearchActionTypes =
    | { type: 'ONCHANGE_BUDGET'; payload: OnChangeBudgetArgs }
    | { type: 'ONCHANGE_ANNUAL_FEE'; payload: S.AnnualFeeKey }
    | { type: 'ONCHANGE_PLAYSTYLE'; payload: S.PlaystyleKey }
    | { type: 'ONCHANGE_PRACTICEPLACE'; payload: S.PracticePlaceKey }
    | { type: 'ONCHANGE_MEMBER_COUNT'; payload: S.MemberCountKey }
    | {
          type: 'ONCHANGE_ADMISSION_CONDITION';
          payload: S.AdmissionConditionKey;
      }
    | { type: 'ONCHANGE_OTHERS'; payload: S.OtherKey }
    | { type: 'ONCHANGE_NO_DEPOSIT'; payload: boolean }
    | { type: 'ONCHANGE_HIGHWAY'; payload: S.Highway }
    | { type: 'ONCHANGE_LINE'; payload: S.Line }
    | { type: 'ONCHANGE_SERIES'; payload: S.Series }
    | { type: 'ONCHANGE_NAME_TRANSFER_FEE'; payload: S.NameTransferFeekey }
    | { type: 'ONCHANGE_WORD'; payload: string }
    | { type: 'RESET_SEARCH_STATE'; payload: void }
    | { type: 'RESET'; payload: S.SearchState };

export const SearchReducer = (state: S.SearchState, action: SearchActionTypes): S.SearchState => {
    switch (action.type) {
        case 'RESET':
            return { ...action.payload };
        case 'ONCHANGE_BUDGET':
            return selectOne(state, ['budget', action.payload]);
        case 'ONCHANGE_NAME_TRANSFER_FEE':
            return selectOne(state, ['nameTransferFee', action.payload]);
        case 'ONCHANGE_ANNUAL_FEE':
            return selectOne(state, ['annualFee', action.payload]);
        case 'ONCHANGE_MEMBER_COUNT':
            return selectOne(state, ['memberCount', action.payload]);
        case 'ONCHANGE_PLAYSTYLE':
            return selectMany(state, 'playstyle', action.payload);
        case 'ONCHANGE_PRACTICEPLACE':
            return selectMany(state, 'practicePlace', action.payload);
        case 'ONCHANGE_ADMISSION_CONDITION':
            return selectMany(state, 'admissionCondition', action.payload);
        case 'ONCHANGE_OTHERS':
            return selectMany(state, 'others', action.payload);
        case 'ONCHANGE_NO_DEPOSIT':
            return selectOne(state, ['noDeposit', action.payload]);
        case 'ONCHANGE_HIGHWAY':
            return selectOne(state, ['highway', action.payload]);
        case 'ONCHANGE_LINE':
            return selectOne(state, ['line', action.payload]);
        case 'ONCHANGE_SERIES':
            return selectOne(state, ['series', action.payload]);
        case 'ONCHANGE_WORD':
            return selectOne(state, ['word', action.payload]);
        case 'RESET_SEARCH_STATE':
            return InitialSearchState([], [], []);
    }
    return state;
};

type ToKeyValueTupleWithKey<T, K extends keyof T> = K extends keyof T ? [K, Pick<T, K>[K]] : never;
export type ToKeyValueTuple<T> = ToKeyValueTupleWithKey<T, keyof T>;

type MultiUpdateArgs = Pick<S.SearchState, 'admissionCondition' | 'others' | 'playstyle' | 'practicePlace'>;
type SingleArgs = Omit<S.SearchState, keyof MultiUpdateArgs>;
type SearchUpdateArgs = ToKeyValueTuple<SingleArgs>;
const selectOne = (state: S.SearchState, keyval: SearchUpdateArgs): S.SearchState => {
    return {
        ...state,
        [keyval[0]]: keyval[1],
    };
};

const selectMany = (
    state: S.SearchState,
    key: keyof MultiUpdateArgs,
    value: S.PracticePlaceKey | S.AdmissionConditionKey | S.OtherKey | S.PlaystyleKey
): S.SearchState => {
    return {
        ...state,
        [key]: toggle(state[key], value),
    };
};
